import { ApiUrlInput } from '@neo/blocks/api-url-input'
import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Label, OptionalLabel } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { Switch } from '@neo/primitives/switch'
import { Tooltip } from '@neo/primitives/tooltip'
import { useQueries } from '@tanstack/react-query'
import { ExternalLink, InfoIcon, Pencil, PlusCircle } from 'lucide-react'
import { useCallback, useEffect, useMemo } from 'react'
import { PhoneNumberSelect } from 'v2/shared/components/phonenumber-select'
import type {
  Credential,
  IPAccessControlList,
  ROriginationURIInternal,
} from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { z } from 'zod'
import { PlatformOutboundAuthCard } from './components/outbound-auth-section'
import { findDefaultPlatformUri } from './default-platform-uris'
import {
  detectForeignPlatform,
  displayOriginationUri,
  hasDefaultPlatformUris,
  hostNeedsSipUserFetch,
  isInboundOnlyPlatform,
  isManagedAclPlatform,
  matchesPlatformUri,
  normalizeUriInput,
  SIP_PLATFORMS,
  type SipPlatformKey,
  uriMismatchMessage,
} from './platform-config'
import {
  getSipPlatformFields,
  isPlatformGateOpen,
  type PlatformGateOptions,
} from './platform-field.config'
import { getOriginationURIQueryOptions } from './queries'
import type { UnifiedApplicationFormValues } from './types'
import { useEnsureDefaultPlatformUris } from './use-ensure-default-platform-uris'

const TrunkAuthenticationSection = ({
  form,
  originationURIs: allOriginationURIs,
  onCreateNewOriginationURI,
  onEditOriginationURI,
  isEditing,
  isCustomPlatformLocked,
}: {
  form: GenericFormType<UnifiedApplicationFormValues>
  originationURIs: ROriginationURIInternal[]
  onCreateNewOriginationURI: () => void
  onEditOriginationURI: (uri: ROriginationURIInternal) => void
  isEditing: boolean
  isCustomPlatformLocked: boolean
}) => {
  const applicationType = form.useStore((state) => state.values.application_type)
  const trunkDirection = form.useStore((state) => state.values.trunk_direction)
  const sipPlatform = form.useStore(
    (state) => state.values.sip_platform as SipPlatformKey | undefined,
  )
  const primaryUri = form.useStore((state) =>
    'primary_uri' in state.values ? (state.values.primary_uri as number | undefined) : undefined,
  )

  // Computed before the early return so it can gate the queries' `enabled`
  // (hooks must run unconditionally; the network calls don't have to).
  const sectionVisible =
    applicationType === 'sip' &&
    trunkDirection === 'inbound' &&
    isPlatformGateOpen(form.state.values, isEditing, isCustomPlatformLocked)

  // Dropdowns only offer URIs belonging to the selected platform; custom shows
  // the full list. Filtering applies while editing too (the platform select is
  // visible there now), but a URI the trunk ALREADY points at is always kept:
  // an existing fallback URI can belong to another platform, and dropping it
  // would fail the "no longer exists" refine and silently block the update.
  const fallbackUri = form.useStore((state) =>
    'fallback_uri' in state.values ? (state.values.fallback_uri as number | undefined) : undefined,
  )
  const isShown = useCallback(
    (uri: ROriginationURIInternal) =>
      !sipPlatform ||
      matchesPlatformUri(uri.uri, sipPlatform) ||
      [primaryUri, fallbackUri].filter(Boolean).map(String).includes(String(uri.id)),
    [sipPlatform, primaryUri, fallbackUri],
  )

  // LIST never returns sip_user (SER-6495); fetched per-URI, sharing the Edit
  // modal's cache, and only for URIs the current platform filter will show.
  // hostNeedsSipUserFetch, not detectPlatformFromUri, so the EU host (excluded
  // there for edit safety) still gets its label fetched where it is listed.
  const sipUserUriUuids = useMemo(
    () =>
      allOriginationURIs.reduce<string[]>((uuids, uri) => {
        if (hostNeedsSipUserFetch(uri.uri) && isShown(uri)) uuids.push(uri.uuid)
        return uuids
      }, []),
    [allOriginationURIs, isShown],
  )
  const sipUserQueries = useQueries({
    queries: sipUserUriUuids.map((uuid) => ({
      ...getOriginationURIQueryOptions(uuid),
      enabled: sectionVisible,
      staleTime: 60_000,
    })),
  })
  const originationURIsWithSipUser = useMemo(() => {
    if (sipUserUriUuids.length === 0) return allOriginationURIs
    const sipUserByUuid = new Map<string, string>()
    sipUserUriUuids.forEach((uuid, i) => {
      const sipUser = sipUserQueries[i]?.data?.data?.sip_user
      if (sipUser) sipUserByUuid.set(uuid, sipUser)
    })
    if (sipUserByUuid.size === 0) return allOriginationURIs
    return allOriginationURIs.map((uri) =>
      sipUserByUuid.has(uri.uuid) ? { ...uri, sip_user: sipUserByUuid.get(uri.uuid) } : uri,
    )
  }, [allOriginationURIs, sipUserUriUuids, sipUserQueries])

  // Static-host platforms get their default URI provisioned on the account the
  // moment they are picked, so the dropdown below can offer it with no Create
  // URI step. Runs in edit too: an old trunk moved onto Vapi wants it as well.
  const { isEnsuring, uris: accountUris } = useEnsureDefaultPlatformUris({
    platform: sipPlatform,
    enabled: sectionVisible,
  })

  // Auto-select the platform's default-transport URI, but only into an EMPTY
  // Primary URI: a customer's pick and an existing trunk's URI are never
  // overridden. Platform switch clears primary_uri (platform-field.config), so
  // this reseeds for the new platform. Deferred a tick because GenericForm
  // resets to defaultValues in its own mount effect, which runs AFTER this
  // child effect and would wipe a synchronous write without the store ever
  // observing it. Meta backfill per failure mode 23.
  useEffect(() => {
    if (!sectionVisible || isEnsuring || primaryUri) return
    if (!hasDefaultPlatformUris(sipPlatform)) return
    const match = findDefaultPlatformUri(
      sipPlatform,
      getLocalAuth().region,
      accountUris ?? allOriginationURIs,
    )
    if (!match) return
    const timer = setTimeout(() => {
      if (form.getFieldValue('primary_uri')) return
      form.setFieldValue('primary_uri', match.id)
      form.setFieldMeta('primary_uri', (prev) => ({
        ...prev,
        errorMap: prev?.errorMap ?? {},
        errors: prev?.errors ?? [],
        isTouched: false,
      }))
      form.validateField('primary_uri', 'change')
    }, 0)
    return () => clearTimeout(timer)
  }, [sectionVisible, isEnsuring, primaryUri, sipPlatform, accountUris, allOriginationURIs, form])

  if (!sectionVisible) return null

  const originationURIs = originationURIsWithSipUser.filter(isShown)

  const authenticationFields: GenericFieldConfig<UnifiedApplicationFormValues> = [
    {
      name: 'primary_uri',
      type: 'custom',
      className: 'col-span-2',
      onChangeListenTo: ['fallback_uri'],
      afterOnChange: (values, _, form) => {
        if (
          'primary_uri' in values &&
          values.application_type === 'sip' &&
          values.trunk_direction === 'inbound'
        ) {
          const primary_uri = values.primary_uri as number | string | undefined
          if (
            primary_uri &&
            !originationURIs.some((uri) => String(uri.id) === String(primary_uri))
          ) {
            form?.setFieldValue('primary_uri', undefined)
            form?.validateField('primary_uri', 'change')
          } else {
            form?.validateField('fallback_uri', 'change')
          }
        }
      },
      component: ({ form }) => {
        const primary_uri = form.getFieldValue('primary_uri') as number | string | undefined
        const uriExists = primary_uri
          ? originationURIs.some((uri) => String(uri.id) === String(primary_uri))
          : true

        const options = originationURIs.map((uri) => ({
          label: (
            <div className="flex items-center justify-between w-full min-w-0">
              <div className="flex flex-col flex-1 min-w-0 mr-2">
                <span className="truncate text-p-500">{uri.name || uri.uri}</span>
                {uri.name && uri.uri && (
                  <span className="truncate text-xs-400 text-muted-foreground">
                    {displayOriginationUri(uri)}
                  </span>
                )}
              </div>
              <button
                className="flex-shrink-0 p-1 hover:bg-accent rounded"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onEditOriginationURI(uri)
                }}
                type="button"
                data-testid={`button-edit-primary-uri-${uri.id}`}
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          ),
          value: String(uri.id),
          searchValue: `${uri.name || ''} ${uri.uri || ''}`.trim(),
        }))

        const displayValue = primary_uri && uriExists ? String(primary_uri) : ''

        return (
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-1">
              <Label>Primary URI</Label>
              {sipPlatform && hasDefaultPlatformUris(sipPlatform) && (
                <Tooltip
                  content={
                    (SIP_PLATFORMS[sipPlatform].transports?.length ?? 0) > 1
                      ? 'A default URI is created for this platform. Use the pencil to change its transport or add authentication.'
                      : 'A default URI is created for this platform. Use the pencil to add authentication.'
                  }
                >
                  <InfoIcon className="h-3 w-3" />
                </Tooltip>
              )}
            </span>
            <Select
              options={options}
              value={displayValue}
              loading={isEnsuring}
              placeholder="Select Primary URI"
              onChange={(value) => {
                const stringValue = Array.isArray(value) ? value[0] || '' : value
                if (stringValue) {
                  const newValue = Number(stringValue)
                  form.setFieldValue('primary_uri', newValue)
                  form.validateField('primary_uri', 'change')
                } else {
                  form.setFieldValue('primary_uri', undefined)
                  form.validateField('primary_uri', 'change')
                }
              }}
              selectedValueRenderer={({ value }) => {
                const stringValue = Array.isArray(value) ? value[0] || '' : String(value)
                const uri = originationURIs.find((u) => String(u.id) === stringValue)
                return <span>{uri?.name || uri?.uri || ''}</span>
              }}
            />
          </div>
        )
      },
      zodValidation: (values) => {
        if (values.application_type === 'sip' && values.trunk_direction === 'inbound') {
          return z
            .number({ message: 'Primary URI is required' })
            .min(1, 'Primary URI is required')
            .refine(
              (val) => {
                if (!val) return false
                return originationURIs.some((uri) => String(uri.id) === String(val))
              },
              {
                message: 'Selected Primary URI no longer exists. Please select a valid URI.',
              },
            )
            .refine(
              (val) => {
                if (!val) return true
                const fallback_uri = values.fallback_uri as number | string | undefined
                if (!fallback_uri) return true
                return String(val) !== String(fallback_uri)
              },
              {
                message:
                  'Primary URI cannot be the same as Fallback URI. Please choose a different URI.',
              },
            )
        }
        return z.any()
      },
    },
    {
      name: 'fallback_uri',
      type: 'custom',
      className: 'col-span-2',
      onChangeListenTo: ['application_type', 'trunk_direction', 'primary_uri'],
      visible: (values) =>
        values.application_type === 'sip' && values.trunk_direction === 'inbound',
      afterOnChange: (values, _, form) => {
        if (
          'fallback_uri' in values &&
          values.application_type === 'sip' &&
          values.trunk_direction === 'inbound'
        ) {
          const fallback_uri = values.fallback_uri as number | string | undefined
          if (
            fallback_uri &&
            !originationURIs.some((uri) => String(uri.id) === String(fallback_uri))
          ) {
            form?.setFieldValue('fallback_uri', undefined)
            form?.validateField('fallback_uri', 'change')
          } else {
            form?.validateField('primary_uri', 'change')
          }
        }
      },
      component: ({ form }) => {
        const fallback_uri = form.getFieldValue('fallback_uri') as number | string | undefined
        const uriExists = fallback_uri
          ? originationURIs.some((uri) => String(uri.id) === String(fallback_uri))
          : true

        const displayValue = fallback_uri && uriExists ? String(fallback_uri) : ''

        const options = originationURIs.map((uri) => ({
          label: (
            <div className="flex items-center justify-between w-full min-w-0">
              <div className="flex flex-col flex-1 min-w-0 mr-2">
                <span className="truncate text-p-500">{uri.name || uri.uri}</span>
                {uri.name && uri.uri && (
                  <span className="truncate text-xs-400 text-muted-foreground">
                    {displayOriginationUri(uri)}
                  </span>
                )}
              </div>
              <button
                className="flex-shrink-0 p-1 hover:bg-accent rounded"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onEditOriginationURI(uri)
                }}
                type="button"
                data-testid={`button-edit-fallback-uri-${uri.id}`}
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          ),
          value: String(uri.id),
          searchValue: `${uri.name || ''} ${uri.uri || ''}`.trim(),
        }))

        return (
          <div className="flex flex-col gap-2">
            <Label>Fallback URI (Optional)</Label>
            <Select
              options={options}
              value={displayValue}
              loading={isEnsuring}
              placeholder="Select Fallback URI"
              onChange={(value) => {
                const stringValue = Array.isArray(value) ? value[0] || '' : value
                const newValue = stringValue ? (Number(stringValue) as number) : undefined
                if (form.getFieldValue('fallback_uri') !== newValue) {
                  form.setFieldValue('fallback_uri', newValue)
                  form.validateField('fallback_uri', 'change')
                }
              }}
              selectedValueRenderer={({ value }) => {
                const stringValue = Array.isArray(value) ? value[0] || '' : String(value)
                const uri = originationURIs.find((u) => String(u.id) === stringValue)
                return <span>{uri?.name || uri?.uri || ''}</span>
              }}
            />
          </div>
        )
      },
      zodValidation: (values) => {
        if (values.application_type === 'sip' && values.trunk_direction === 'inbound') {
          return z
            .number()
            .optional()
            .refine(
              (val) => {
                if (!val) return true
                return originationURIs.some((uri) => String(uri.id) === String(val))
              },
              {
                message: 'Selected Fallback URI no longer exists. Please select a valid URI.',
              },
            )
            .refine(
              (val) => {
                if (!val) return true
                const primary_uri = values.primary_uri as number | string | undefined
                if (!primary_uri) return true
                return String(val) !== String(primary_uri)
              },
              {
                message:
                  'Fallback URI cannot be the same as Primary URI. Please choose a different URI.',
              },
            )
        }
        return z.any()
      },
    },
  ]

  return (
    <div className="grid gap-2 col-span-2">
      <Label className="flex items-center justify-between">
        Trunk URIs
        {/* same Button treatment as "Buy new number" (Tejal, 2026-08-03) */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault()
            onCreateNewOriginationURI()
          }}
          className="text-primary h-auto p-0"
          data-testid="button-create-new-uri"
        >
          <PlusCircle className="mr-1 h-3 w-3" />
          Create new URI
        </Button>
      </Label>
      <div className="bg-bg3 rounded-md p-3 grid gap-4">
        <FieldsRenderer fields={authenticationFields} form={form} />
      </div>
    </div>
  )
}

const InboundMessageSection = ({
  form,
  isEditing,
  isCustomPlatformLocked,
  isMessagingLocked,
}: {
  form: GenericFormType<UnifiedApplicationFormValues>
  isEditing: boolean
  isCustomPlatformLocked: boolean
  isMessagingLocked: boolean
}) => {
  const applicationType = form.useStore((state) => state.values.application_type)
  const trunkDirection = form.useStore((state) => state.values.trunk_direction)
  // subscribe so the section re-evaluates the platform gate as it changes
  form.useStore((state) => state.values.sip_platform)

  if (applicationType !== 'sip' || trunkDirection !== 'inbound') return null
  if (isMessagingLocked) return null
  if (!isPlatformGateOpen(form.state.values, isEditing, isCustomPlatformLocked)) return null

  const messageFields: GenericFieldConfig<UnifiedApplicationFormValues> = [
    {
      name: 'message_url',
      type: 'custom',
      label: <OptionalLabel>Inbound SMS</OptionalLabel>,
      className: 'col-span-2',
      component: ({ form }) => (
        <ApiUrlInput
          methods={['GET', 'POST']}
          defaultMethod={form.state.values.message_method || 'POST'}
          url={form.state.values.message_url || ''}
          placeholder="Enter a URL"
          onMethodChange={(method) => {
            form.setFieldValue('message_method', method as 'GET' | 'POST')
          }}
          onUrlChange={(url) => {
            form.setFieldValue('message_url', url)
            form.validateField('message_url', 'change')
          }}
          id="message-url-input"
          label="Inbound SMS (Optional)"
        />
      ),
      zodValidation: () =>
        z
          .union([
            z.string().url('Please enter a valid URL'),
            z.literal(''),
            z.null(),
            z.undefined(),
          ])
          .optional(),
    },
  ]

  return (
    <div className="grid gap-2 col-span-2">
      <Label>Message</Label>
      <div className="bg-bg3 rounded-md p-3 grid gap-4">
        <FieldsRenderer fields={messageFields} form={form} />
      </div>
    </div>
  )
}

export const getTrunkFields = (
  ipAccessControlLists: IPAccessControlList[] = [],
  credentials: Credential[] = [],
  originationURIs: ROriginationURIInternal[] = [],
  onCreateNewIPACL: () => void,
  onEditIPACL: (ipacl: IPAccessControlList) => void,
  onCreateNewCredential: () => void,
  onEditCredential: (credential: Credential) => void,
  onCreateNewOriginationURI: () => void,
  onEditOriginationURI: (uri: ROriginationURIInternal) => void,
  isEditing: boolean = false,
  onTrunkDirectionChange?: (isOutbound: boolean) => void,
  _onApplicationTypeChange?: (isSipTrunk: boolean) => void,
  gateOptions: PlatformGateOptions = {},
): GenericFieldConfig<UnifiedApplicationFormValues> => {
  const isCustomPlatformLocked = gateOptions.isCustomPlatformLocked ?? false
  const isMessagingLocked = gateOptions.isMessagingLocked ?? false
  const gateOpen = (values: UnifiedApplicationFormValues) =>
    isPlatformGateOpen(values, isEditing, isCustomPlatformLocked)

  return [
    ...getSipPlatformFields(
      isEditing,
      isCustomPlatformLocked,
      gateOptions.onCloseHost,
      onTrunkDirectionChange,
    ),
    {
      name: 'trunk_direction',
      label: 'Trunk Direction',
      className: 'col-span-2',
      onChangeListenTo: ['application_type', 'sip_platform'],
      componentType: 'radioGroup',
      // Options via getDynamicProps, NOT static inputProps: field-component
      // lodash-merges static inputProps OVER the dynamic ones (and merge unions
      // arrays by index), so a static options list would always win.
      getDynamicProps: (values) => ({
        inputProps: {
          options: isInboundOnlyPlatform(values.sip_platform as SipPlatformKey | undefined)
            ? [{ label: 'Inbound', value: 'inbound' }]
            : [
                { label: 'Inbound', value: 'inbound' },
                { label: 'Outbound', value: 'outbound' },
              ],
        },
      }),
      visible: (values) => gateOpen(values) && !isEditing,
      zodValidation: (values) => {
        if (values.application_type !== 'sip') return z.any()
        // Inbound-only platforms enforce the invariant in the SCHEMA too, not
        // just the radio options: a direction arriving outside the radio (stale
        // form state, a future preset path) would otherwise validate and take
        // the outbound payload path in submit.
        if (isInboundOnlyPlatform(values.sip_platform as SipPlatformKey | undefined)) {
          return z.literal('inbound', {
            errorMap: () => ({ message: 'This platform supports inbound trunks only' }),
          })
        }
        return z.enum(['inbound', 'outbound']).default('outbound')
      },
      afterOnChange: (values, _, form) => {
        if (onTrunkDirectionChange && 'trunk_direction' in values) {
          onTrunkDirectionChange(values.trunk_direction === 'outbound')
          // Reset trunk_authentication_type field state when switching directions
          form?.setFieldMeta('trunk_authentication_type', (prev) => ({
            ...prev,
            errorMap: {},
            errors: [],
            isTouched: false,
          }))
        }
      },
    },
    {
      // LiveKit outbound URI (per design, outside the auth card): the project
      // subdomain, collected to verify the user is really on LiveKit. UI-only —
      // the outbound trunk API takes no URI; nothing is sent.
      name: 'outbound_uri',
      type: 'custom',
      className: 'col-span-2',
      // No sip_platform/trunk_direction in onChangeListenTo: linked validation
      // would surface "required" the moment the field appears. Submit still
      // validates (cause 'submit' runs the same validator); visibility updates
      // come from the form re-render, not listenTo.
      onChangeListenTo: ['application_type'],
      visible: (values) =>
        gateOpen(values) &&
        !isEditing &&
        values.trunk_direction === 'outbound' &&
        values.sip_platform === 'livekit',
      component: ({ form }) => {
        const subdomain = (form.getFieldValue('outbound_uri') as string) || ''
        return (
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-1">
              <Label htmlFor="livekit-outbound-uri-input">URI</Label>
              <Tooltip content="Type just the subdomain, or paste your full LiveKit URL and we'll trim it for you.">
                <InfoIcon className="h-3 w-3" />
              </Tooltip>
            </span>
            <div className="flex items-stretch overflow-hidden rounded-md border font-jetbrains tracking-normal focus-within:ring-1 focus-within:ring-primary">
              <span className="flex items-center bg-bg3 px-2 text-p-400 text-secondary whitespace-nowrap">
                sip:
              </span>
              <input
                id="livekit-outbound-uri-input"
                aria-label="LiveKit Cloud subdomain"
                value={subdomain}
                inputMode="url"
                placeholder="{subdomain}"
                onChange={(e) => {
                  const { value } = normalizeUriInput(e.target.value, 'livekit')
                  form.setFieldValue('outbound_uri', value)
                  form.validateField('outbound_uri', 'change')
                }}
                className="flex-1 min-w-0 bg-transparent px-2 py-2 text-p-400 text-foreground outline-none"
                data-testid="input-livekit-outbound-uri"
              />
              <span className="flex items-center bg-bg3 px-2 text-p-400 text-secondary whitespace-nowrap">
                .sip.livekit.cloud
              </span>
            </div>
          </div>
        )
      },
      zodValidation: (values) => {
        if (
          values.application_type !== 'sip' ||
          values.trunk_direction !== 'outbound' ||
          values.sip_platform !== 'livekit' ||
          isEditing
        ) {
          return z.any()
        }
        return z
          .string({ required_error: 'LiveKit subdomain is required' })
          .min(1, 'LiveKit subdomain is required')
          .refine(
            (val) => !detectForeignPlatform(val, 'livekit'),
            (val) => ({
              message: uriMismatchMessage(
                detectForeignPlatform(val, 'livekit')?.label ?? 'different platform',
                'LiveKit Cloud',
              ),
            }),
          )
          .refine((val) => /^[a-zA-Z0-9._-]+$/.test(val), {
            message: 'Subdomain can contain only letters, numbers, dots, hyphens and underscores',
          })
      },
    },
    {
      name: 'trunk_authentication_type',
      type: 'custom',
      className: 'col-span-2',
      // application_type only (like the pre-redesign select): listening to
      // sip_platform/trunk_direction made the required check fire eagerly on
      // platform pick, before the user touched anything.
      onChangeListenTo: ['application_type'],
      // Checkbox card per the design, for every platform: managed = ACL
      // force-checked + read-only Plivo list, LiveKit = credentials only,
      // custom = both freely toggleable. The nested ACL/credential selects are
      // plain components (NOT mounted fields — a second field instance per name
      // corrupts TanStack 0.28's registry, see failure modes 22-23),
      // so their required/exists checks are folded into THIS field's validator
      // and the error renders below the card.
      // Edit renders the same card as create (it used to fall back to two
      // standalone selects, which offered a LiveKit trunk an IP ACL that
      // creation refuses it). Defaults seed the checkbox state from the stored
      // ipacl_uuid/credential_uuid so nothing reads back unchecked.
      visible: (values) => gateOpen(values) && values.trunk_direction === 'outbound',
      component: ({ form }) => (
        <PlatformOutboundAuthCard
          form={form}
          ipAccessControlLists={ipAccessControlLists}
          credentials={credentials}
          onCreateNewIPACL={onCreateNewIPACL}
          onEditIPACL={onEditIPACL}
          onCreateNewCredential={onCreateNewCredential}
          onEditCredential={onEditCredential}
        />
      ),
      zodValidation: (values) => {
        if (values.application_type !== 'sip' || values.trunk_direction !== 'outbound') {
          return z.any()
        }
        const authEnum = z.enum(['ip_access_control_list', 'credentials', 'both'], {
          required_error: 'Select at least one authentication type',
        })
        // Managed platforms authenticate by the implicit Plivo ACL, so an unset
        // value is valid there (no auto-set needed to satisfy the form); every
        // other platform must have an explicit selection.
        const base = isManagedAclPlatform(values.sip_platform) ? authEnum.optional() : authEnum
        return base.superRefine((val, ctx) => {
          const wantsAcl = val === 'ip_access_control_list' || val === 'both'
          const wantsCredentials = val === 'credentials' || val === 'both'
          if (wantsAcl && values.sip_platform === 'custom') {
            const ipaclUuid = values.ipacl_uuid as string | undefined
            if (!ipaclUuid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'IP Access Control List is required',
              })
            } else if (!ipAccessControlLists.some((ipacl) => ipacl.ipacl_uuid === ipaclUuid)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Selected IP Access Control List no longer exists. Please select a valid one.',
              })
            }
          }
          if (wantsCredentials) {
            const credentialUuid = values.credential_uuid as string | undefined
            if (!credentialUuid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Authentication credential is required',
              })
            } else if (!credentials.some((cred) => cred.credential_uuid === credentialUuid)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Selected credential no longer exists. Please select a valid one.',
              })
            }
          }
        })
      },
    },
    {
      name: 'trunk_domain',
      componentType: 'input',
      label: 'Termination SIP Domain',
      className: 'col-span-2',
      onChangeListenTo: ['application_type'],
      visible: (values) =>
        values.application_type === 'sip' && isEditing && values.trunk_direction === 'outbound',
      getDynamicProps: (values) => ({
        inputProps: {
          disabled: true,
          inputMode: 'url',
          placeholder: 'Termination SIP Domain',
          value: values.trunk_domain || '',
          endIcon: values.trunk_domain ? (
            <ClipboardCopyButton
              textToCopy={values.trunk_domain}
              data-testid="button-copy-trunk-domain"
            />
          ) : undefined,
        },
      }),
      zodValidation: () => z.any(),
    },
    {
      name: 'enabled',
      componentType: 'switch',
      label: 'Trunk Enabled',
      className: 'col-span-2',
      onChangeListenTo: ['application_type', 'trunk_direction'],
      visible: (values) =>
        values.application_type === 'sip' && isEditing && values.trunk_direction === 'inbound',
      zodValidation: () => z.boolean().optional(),
    },
    {
      name: 'trunk_status',
      type: 'custom',
      className: 'col-span-2',
      onChangeListenTo: ['application_type', 'trunk_direction'],
      visible: (values) =>
        values.application_type === 'sip' && isEditing && values.trunk_direction === 'outbound',
      component: ({ form }) => {
        const trunkStatus = form.getFieldValue('trunk_status') as 'enabled' | 'disabled' | undefined
        const isEnabled = trunkStatus === 'enabled'

        return (
          <Switch
            label="Trunk Enabled"
            value={isEnabled}
            onChange={(checked: boolean) => {
              form.setFieldValue('trunk_status', checked ? 'enabled' : 'disabled')
              form.validateField('trunk_status', 'change')
            }}
            data-testid="switch-trunk-status"
          />
        )
      },
      zodValidation: () => z.enum(['enabled', 'disabled']).optional(),
    },
    {
      name: 'trunk_authentication_section',
      type: 'basic',
      component: ({ form }) => (
        <TrunkAuthenticationSection
          form={form}
          originationURIs={originationURIs}
          onCreateNewOriginationURI={onCreateNewOriginationURI}
          onEditOriginationURI={onEditOriginationURI}
          isEditing={isEditing}
          isCustomPlatformLocked={isCustomPlatformLocked}
        />
      ),
    },
    {
      name: 'inbound_message_section',
      type: 'basic',
      component: ({ form }) => (
        <InboundMessageSection
          form={form}
          isEditing={isEditing}
          isCustomPlatformLocked={isCustomPlatformLocked}
          isMessagingLocked={isMessagingLocked}
        />
      ),
    },
    {
      name: 'secure',
      type: 'custom',
      className: 'col-span-2',
      onChangeListenTo: ['application_type', 'trunk_direction', 'sip_platform'],
      visible: (values) => gateOpen(values) && values.trunk_direction === 'outbound',
      component: ({ form }) => {
        const isSecure = form.getFieldValue('secure') as boolean

        return (
          <Switch
            label={
              <span className="ml-1 flex gap-1">
                <span className="text-p-500 text-foreground">Secure Trunking</span>
                <Tooltip
                  content="If enabled, Secure Real-Time Protocol (SRTP) will be used to encrypt the media leg of the call, and Transport Layer Security (TLS) will be used to encrypt the SIP signaling leg of the call."
                  side="bottom"
                  className="cursor-pointer"
                >
                  <InfoIcon className="h-3 w-3" />
                </Tooltip>
              </span>
            }
            value={isSecure}
            onChange={(checked: boolean) => {
              form.setFieldValue('secure', checked)
              form.validateField('secure', 'change')
            }}
            data-testid="switch-secure-trunking"
          />
        )
      },
      zodValidation: (values) =>
        values.application_type === 'sip' ? z.boolean().default(false) : z.any(),
    },

    {
      name: 'linked_numbers',
      type: 'custom',
      className: 'col-span-2',
      onChangeListenTo: ['application_type', 'trunk_direction', 'sip_platform'],
      visible: (values) => gateOpen(values) && values.trunk_direction === 'inbound',
      component: ({ form }) => {
        const handleBuyNewNumber = () => {
          window.open('/phone-numbers?action=buy', '_blank')
        }

        const linkedNumbers = form.state.values.linked_numbers
        const value = Array.isArray(linkedNumbers) ? linkedNumbers : []

        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="linked-numbers-select">
                Link Numbers <span className="text-muted-foreground italic">(Optional)</span>
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleBuyNewNumber}
                className="text-primary h-auto p-0"
              >
                Buy new number
                <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
            </div>
            <PhoneNumberSelect
              value={value}
              onChange={(selectedValue) => {
                const phoneNumbers = Array.isArray(selectedValue) ? selectedValue : [selectedValue]
                form.setFieldValue('linked_numbers', phoneNumbers.filter(Boolean))
              }}
              multiple
              valueType="number"
              placeholder="Select phone numbers..."
            />
          </div>
        )
      },
      zodValidation: (values) =>
        values.application_type === 'sip' && values.trunk_direction === 'inbound'
          ? z.array(z.string()).optional()
          : z.any(),
    },
  ]
}
